import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { Tooltip } from '../components/ui/Tooltip';
import { DatePicker } from '../components/ui/DatePicker';
import { Plus, Filter, Download, Eye, Edit, Search, Receipt, CheckCircle2, Calendar, X, Save, Mail, List as ListIcon, Euro, Trash2, AlertCircle, FileText, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data with new statuses and Nota di credito
const initialInvoicesData = Array.from({
  length: 50
}, (_, i) => {
  let type = 'Fattura';
  let codePrefix = 'F';
  if (i % 5 === 2) {
    type = 'Nota di credito';
    codePrefix = 'NC';
  } else if (i % 2 !== 0) {
    type = 'Proforma';
    codePrefix = 'P';
  }
  return {
    id: i + 1,
    code: `${codePrefix}-2025/${String(i + 1).padStart(3, '0')}`,
    type: type,
    client: `Azienda ${i} SRL`,
    date: '31/01/2025',
    amount: `€ ${1200 + i * 50}`,
    status: ['da_pianificare', 'pianificato', 'proforma', 'fatturato', 'pagato', 'sollecitare', 'archiviato'][i % 7],
    dueDate: '28/02/2025',
    approval: i % 4 === 0 ? 'pending' : 'approved',
    sollecito1: i % 7 === 5,
    sollecito2: i % 7 === 5 && i % 2 === 0,
    sollecito3: false,
    contractNumber: `CTR-2024/${String(i).padStart(3, '0')}`,
    proformaNumber: type === 'Fattura' ? `P-2025/${String(i).padStart(3, '0')}` : '',
    invoiceNumber: type === 'Nota di credito' ? `F-2025/${String(i - 1).padStart(3, '0')}` : '',
    withVat: true,
    paymentDate: i % 7 === 4 ? '2025-02-15' : '' // If status is pagato
  };
});
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'type',
  label: 'Tipo'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'date',
  label: 'Data emiss.'
}, {
  key: 'amount',
  label: 'Importo'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'approval',
  label: 'Avallo'
}, {
  key: 'dueDate',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const statusLabels: Record<string, string> = {
  da_pianificare: 'Da fare',
  pianificato: 'Pianificato',
  proforma: 'Proforma',
  fatturato: 'Effettuata',
  pagato: 'Pagata',
  sollecitare: 'Sollecitare',
  archiviato: 'Archiviato',
  revisione_pagamento: 'Revisione pagamento',
  non_pagata: 'Non pagata',
  recupero_crediti: 'Recupero crediti'
};
const statusColors: Record<string, string> = {
  da_pianificare: 'neutral',
  pianificato: 'info',
  proforma: 'warning',
  fatturato: 'primary',
  pagato: 'success',
  sollecitare: 'danger',
  archiviato: 'neutral',
  revisione_pagamento: 'warning',
  non_pagata: 'danger',
  recupero_crediti: 'danger'
};
export function InvoicingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [invoicesData, setInvoicesData] = useState(initialInvoicesData);
  const [formData, setFormData] = useState<any>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(invoicesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['code', 'client']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  // Auto-set payment date when status becomes 'pagato'
  useEffect(() => {
    if (formData.status === 'pagato' && !formData.paymentDate) {
      setFormData(prev => ({
        ...prev,
        paymentDate: new Date().toISOString().split('T')[0]
      }));
    }
  }, [formData.status]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    // Initialize form data with item values
    setFormData({
      ...item,
      dueDateType: 'Specifica' // Default for existing items
    });
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      type: 'Fattura',
      status: 'da_pianificare',
      sendEmailOnExpiry: true,
      sendEmailToSEF: true,
      withVat: true,
      dueDateType: 'Immediata',
      sollecito1: false,
      sollecito2: false,
      sollecito3: false
    });
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }, 1500);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const getStatusOptions = (type: string) => {
    if (type === 'Proforma' || type === 'Nota di credito') {
      return [{
        value: 'da_pianificare',
        label: 'Da fare'
      }, {
        value: 'fatturato',
        label: 'Effettuata'
      }, {
        value: 'archiviato',
        label: 'Archiviata'
      }];
    }
    // Fattura
    return [{
      value: 'da_pianificare',
      label: 'Da fare'
    }, {
      value: 'fatturato',
      label: 'Effettuata'
    }, {
      value: 'revisione_pagamento',
      label: 'Revisione pagamento'
    }, {
      value: 'pagato',
      label: 'Pagata'
    }, {
      value: 'non_pagata',
      label: 'Non pagata'
    }, {
      value: 'sollecitare',
      label: 'Sollecitare'
    }, {
      value: 'recupero_crediti',
      label: 'Recupero crediti'
    }, {
      value: 'archiviato',
      label: 'Archiviata'
    }];
  };
  // Helper to get filter status options based on selected type
  const getFilterStatusOptions = () => {
    if (!filters.type) {
      // Combine all unique statuses if no type selected
      const allStatuses = new Set<string>();
      getStatusOptions('Fattura').forEach(opt => allStatuses.add(opt.value));
      getStatusOptions('Proforma').forEach(opt => allStatuses.add(opt.value));
      // Map back to option objects
      return Array.from(allStatuses).map(status => ({
        value: status,
        label: statusLabels[status] || status
      }));
    }
    return getStatusOptions(filters.type);
  };
  const getCodeLabel = (type: string) => {
    switch (type) {
      case 'Fattura':
        return 'Numero fattura';
      case 'Proforma':
        return 'Numero proforma';
      case 'Nota di credito':
        return 'Numero nota di credito';
      default:
        return 'Numero';
    }
  };
  const getProgressiveType = (type: string) => {
    switch (type) {
      case 'Fattura':
        return 'invoice';
      case 'Proforma':
        return 'proforma';
      case 'Nota di credito':
        return 'credit_note';
      default:
        return 'invoice';
    }
  };
  return <Layout data-id="element-4879" data-mp="3mhvlzh357ldj">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-4880" data-mp="3mjgez29t6w9a">
        <div data-id="element-4881" data-mp="3rgyg46hl476q">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-4882" data-mp="3rgf6dd238p3s">
            Fatturazione e pagamenti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-4883" data-mp="55b4qthiaau">
            Gestione ciclo attivo, avalli e recupero crediti.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-4884" data-mp="33rnjn179oyye">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-4886" data-mp="2w7bo1dcdvlzr" />} data-id="element-4885" data-mp="33qkxf6kw3kot">
            Esporta elenco
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-4888" data-mp="2m9xfdzwpc1i0" />} data-id="element-4887" data-mp="2rauvm6y5ilgv">
            Nuovo documento
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-4889" data-mp="2i83j03tjhjnn">
        <div className="p-4 flex flex-col gap-4" data-id="element-4890" data-mp="2ib7u7l5f9fnt">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-4891" data-mp="2datvwutmhk2z">
            <div className="flex-1" data-id="element-4892" data-mp="28j228bf66y8m">
              <Input placeholder="Cerca documento, cliente..." startContent={<Search className="h-4 w-4" data-id="element-4894" data-mp="23l0nu3sovn3h" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-4893" data-mp="28i0rk822gdrf" />
            </div>
            <div className="flex gap-2" data-id="element-4895" data-mp="1ui98hswp076k">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-4897" data-mp="spxwo8wl07j3" />} data-id="element-4896" data-mp="1ul3sjjbsnpzg">
                Filtri{' '}
                {Object.keys(filters).filter(k => filters[k]).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-4898" data-mp="12gitprwlx3zw">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="invoicing" lockedColumns={['code']} data-id="element-4899" data-mp="9tojroxf1n0o" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-4900" data-mp="eum15zalqd14">
              <Select label="Tipo" value={filters.type || ''} onChange={e => {
            setFilter('type', e.target.value);
            // Reset status if not valid for new type
            if (e.target.value && filters.status) {
              const validStatuses = getStatusOptions(e.target.value).map(o => o.value);
              if (!validStatuses.includes(filters.status)) {
                setFilter('status', '');
              }
            }
          }} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Fattura',
            label: 'Fattura'
          }, {
            value: 'Proforma',
            label: 'Proforma'
          }, {
            value: 'Nota di credito',
            label: 'Nota di credito'
          }]} data-id="element-4901" data-mp="1q6mvdtq8ito9" />
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...getFilterStatusOptions()]} data-id="element-4902" data-mp="3a1epdnu60nqf" />
              <div className="col-span-full flex justify-end" data-id="element-4903" data-mp="2v68fgpry84rt">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-4905" data-mp="2hd914r3wkglu" />} data-id="element-4904" data-mp="2qcm3kztm04x2">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-4906" data-mp="27e1flgn0lfnn">
        <div className="overflow-x-auto" data-id="element-4907" data-mp="27fbfmbdfgxwx">
          <table className="w-full text-left border-collapse table-auto" data-id="element-4908" data-mp="1xauzdgw1desl">
            <thead data-id="element-4909" data-mp="1xcz2hwm429rt">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-4910" data-mp="229fs6v8ykw70">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-4911" data-mp="2c5stofaqwecb">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4912" data-mp="1ogzt25j6hibd">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-4913" data-mp="1tb45h4b7qiju">
                  {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-4914" data-mp="10qdnqm0k6c52">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-4 py-4" data-id="element-4915" data-mp="3c7pa4k1exrjg">
                      <span className={`text-xs font-medium px-2 py-1 rounded 
                          ${item.type === 'Fattura' ? 'bg-blue-50 text-blue-700' : item.type === 'Nota di credito' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-4916" data-mp="3c8h5puv0fp9q">
                        {item.type}
                      </span>
                    </td>}
                  {visibleColumns.includes('client') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-4917" data-mp="3q6yn7aubo5j7">
                      {item.client}
                    </td>}
                  {visibleColumns.includes('date') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4918" data-mp="1vur0ulw2gwz1">
                      {item.date}
                    </td>}
                  {visibleColumns.includes('amount') && <td className="px-4 py-4 text-sm font-mono text-slate-900" data-id="element-4919" data-mp="g96oeq0hkq63">
                      {item.amount}
                    </td>}
                  {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-4920" data-mp="6ilpf1gwhjmn">
                      <Badge variant={statusColors[item.status] as any || 'neutral'} data-id="element-4921" data-mp="3oprvu2dgzb73">
                        {statusLabels[item.status] || item.status}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('approval') && <td className="px-4 py-4" data-id="element-4922" data-mp="1d10hhxnzxes8">
                      {item.approval === 'pending' && <Badge variant="warning" data-id="element-4923" data-mp="1i0dc54l3zq5d">Da avallare</Badge>}
                      {item.approval === 'approved' && <Badge variant="success" data-id="element-4924" data-mp="1gjzkwe8pmn2f">Avallato</Badge>}
                    </td>}
                  {visibleColumns.includes('dueDate') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-4925" data-mp="16ks75py2cqes">
                      {item.dueDate}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-4926" data-mp="1zba6qgzkw9ng">
                      <div className="flex items-center gap-1" data-id="element-4927" data-mp="1u9t8kt8hb1ak">
                        <Tooltip content="Visualizza documento" data-id="element-4928" data-mp="1pjcvja8klhoq">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-4929" data-mp="1phjmjoahildk">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-4930" data-mp="2d8fb8cjn90gl" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Modifica documento" data-id="element-4931" data-mp="3vx9r35e8vsah">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-4932" data-mp="4ovwwgcscia7">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-4933" data-mp="drdgso4ywyu1" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Elimina documento" data-id="element-4934" data-mp="nnqavn4fdepb">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-4935" data-mp="nm5fxwe77tqx">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-4936" data-mp="11gpqdp47j8wv" />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-4937" data-mp="cdakuwhtolwg">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-4938" data-mp="celtpaq4kjuf" />
        </div>
      </Card>

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio documento" size="2xl" data-id="element-4939" data-mp="3uo4c885rtrk0">
        {selectedItem && <div className="space-y-8" data-id="element-4940" data-mp="7fiwudkatjxj">
            <section data-id="element-4941" data-mp="2m67g17sp93q">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4942" data-mp="2lwgobexexwz">
                <Receipt className="h-4 w-4" data-id="element-4943" data-mp="3b0omimw5lowu" /> Dati documento
              </h4>
              <div className="grid grid-cols-2 gap-6" data-id="element-4944" data-mp="3704o7fzugf94">
                <div data-id="element-4945" data-mp="2wx95mk02cv6v">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4946" data-mp="2wwpkyvj13sp5">
                    {getCodeLabel(selectedItem.type)}
                  </span>
                  <p className="font-mono font-medium" data-id="element-4947" data-mp="31pio5eyumsz4">{selectedItem.code}</p>
                </div>
                <div data-id="element-4948" data-mp="2mwytz3tofvtt">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4949" data-mp="2rxd02gbpwvp7">
                    Tipo documento
                  </span>
                  <p className="font-medium" data-id="element-4950" data-mp="3fo0ab893vxex">{selectedItem.type}</p>
                </div>
                <div data-id="element-4951" data-mp="3kl8xjn942rn1">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4952" data-mp="3kkha4zpo407u">
                    Cliente
                  </span>
                  <p className="font-medium" data-id="element-4953" data-mp="3ao49fhvpgm3l">{selectedItem.client}</p>
                </div>
                <div data-id="element-4954" data-mp="1uadzvvlth5mk">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4955" data-mp="1u9urp6lyxvlq">
                    Stato
                  </span>
                  <div className="mt-1" data-id="element-4956" data-mp="1zaij3te40egh">
                    <Badge variant={statusColors[selectedItem.status] as any || 'neutral'} data-id="element-4957" data-mp="z4qw2xpaufpb">
                      {statusLabels[selectedItem.status] || selectedItem.status}
                    </Badge>
                  </div>
                </div>

                {selectedItem.type === 'Fattura' && selectedItem.status === 'sollecitare' && <div className="col-span-full bg-orange-50 p-4 rounded-xl border border-orange-200" data-id="element-4958" data-mp="gcolo4393vni">
                      <span className="text-xs font-semibold text-orange-600 uppercase block mb-2" data-id="element-4959" data-mp="l610jmi4oo32">
                        Stato solleciti
                      </span>
                      <div className="flex gap-4" data-id="element-4960" data-mp="bfy41cbssrzz">
                        <div className="flex items-center gap-2" data-id="element-4961" data-mp="3tkjbbvzgehqd">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedItem.sollecito1 ? 'bg-orange-500 border-orange-500' : 'border-orange-300'}`} data-id="element-4962" data-mp="3tl1df98srq67">
                            {selectedItem.sollecito1 && <CheckCircle2 className="w-3 h-3 text-white" data-id="element-4963" data-mp="3a0kfrvnb4un6" />}
                          </div>
                          <span className="text-sm text-orange-800" data-id="element-4964" data-mp="3jx6yoew2pbmk">
                            Sollecito 1
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-4965" data-mp="1p7s4vv1vssy">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedItem.sollecito2 ? 'bg-orange-500 border-orange-500' : 'border-orange-300'}`} data-id="element-4966" data-mp="3swu1n54gaphq">
                            {selectedItem.sollecito2 && <CheckCircle2 className="w-3 h-3 text-white" data-id="element-4967" data-mp="kili0mjwp91p" />}
                          </div>
                          <span className="text-sm text-orange-800" data-id="element-4968" data-mp="gedng8qroyvk">
                            Sollecito 2
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-4969" data-mp="sizraqo0nnmu">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedItem.sollecito3 ? 'bg-orange-500 border-orange-500' : 'border-orange-300'}`} data-id="element-4970" data-mp="xhb1x3m4y264">
                            {selectedItem.sollecito3 && <CheckCircle2 className="w-3 h-3 text-white" data-id="element-4971" data-mp="1b86fgt7kx7g1" />}
                          </div>
                          <span className="text-sm text-orange-800" data-id="element-4972" data-mp="1g8u2z4wln65i">
                            Sollecito 3
                          </span>
                        </div>
                      </div>
                    </div>}
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-4973" data-mp="3mzbhglpeldsf"></div>

            <section data-id="element-4974" data-mp="2435mqhu2gjg6">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4975" data-mp="243notv3etrw0">
                <FileText className="h-4 w-4" data-id="element-4976" data-mp="1zbw1dtfjw2a3" /> Riferimenti e dettagli
              </h4>
              <div className="grid grid-cols-2 gap-6" data-id="element-4977" data-mp="1vc59fmeafg3z">
                <div data-id="element-4978" data-mp="1qbzi67bxjvmf">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4979" data-mp="1qciwle2dg1vp">
                    Numero di contratto
                  </span>
                  <p className="font-medium" data-id="element-4980" data-mp="2n1gs4yhy43tx">
                    {selectedItem.contractNumber || '-'}
                  </p>
                </div>

                {selectedItem.type === 'Fattura' && <div data-id="element-4981" data-mp="2e3oerzvsb696">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4982" data-mp="292h3hqufnoxr">
                      Numero di proforma
                    </span>
                    <p className="font-medium" data-id="element-4983" data-mp="12nzph1v25v57">
                      {selectedItem.proformaNumber || '-'}
                    </p>
                  </div>}

                {selectedItem.type === 'Nota di credito' && <div data-id="element-4984" data-mp="2qaq6hg1ykana">
                    <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4985" data-mp="2qb9rx6qthh62">
                      Numero fattura
                    </span>
                    <p className="font-medium" data-id="element-4986" data-mp="2cl66uj3obti7">
                      {selectedItem.invoiceNumber || '-'}
                    </p>
                  </div>}

                <div data-id="element-4987" data-mp="359bvhgxum2nb">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4988" data-mp="358satsgtd05l">
                    Importo con IVA
                  </span>
                  <div className="mt-1" data-id="element-4989" data-mp="2vack3qlp7swm">
                    <Badge variant={selectedItem.withVat ? 'success' : 'neutral'} data-id="element-4990" data-mp="2v9b7visy50db">
                      {selectedItem.withVat ? 'Sì' : 'No'}
                    </Badge>
                  </div>
                </div>
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-4991" data-mp="1ovbz2fy7ov4t"></div>

            <section data-id="element-4992" data-mp="1h9zpj9uu9mxb">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4993" data-mp="1h96pssuarxoo">
                <Euro className="h-4 w-4" data-id="element-4994" data-mp="1can2ao022du7" /> Dettagli economici
              </h4>
              <div className="grid grid-cols-2 gap-6" data-id="element-4995" data-mp="1q9v65u75ya4q">
                <div data-id="element-4996" data-mp="1lbtlx5y044ro">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4997" data-mp="1lbbjtsonqwbu">
                    Importo
                  </span>
                  <p className="font-mono font-bold text-seform-navy" data-id="element-4998" data-mp="xms7n22hlzeh">
                    {selectedItem.amount}
                  </p>
                </div>
                <div data-id="element-4999" data-mp="17clk05ageijs">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5000" data-mp="17d3nnmzg3z1q">
                    Data emissione
                  </span>
                  <p className="font-medium" data-id="element-5001" data-mp="12jio3qi7ex0f">{selectedItem.date}</p>
                </div>
                <div data-id="element-5002" data-mp="etocfc9mfehr">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5003" data-mp="9wo4evt7o1o0">
                    Data scadenza
                  </span>
                  <p className="font-medium" data-id="element-5004" data-mp="29xw1sajhf9n4">{selectedItem.dueDate}</p>
                </div>
                <div data-id="element-5005" data-mp="2ewyyn7pb69k0">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-5006" data-mp="2ewftkrkrbfne">
                    Data di pagamento
                  </span>
                  <p className="font-medium" data-id="element-5007" data-mp="2onk85q6isyhp">
                    {selectedItem.paymentDate || '-'}
                  </p>
                </div>
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-5008" data-mp="1r2ebwczulejd"></div>

            <section data-id="element-5009" data-mp="1vzxrphc71zdf">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5010" data-mp="1vzel2wrzuxep">
                <Mail className="h-4 w-4" data-id="element-5011" data-mp="210lq4o2r5iho" /> Notifiche automatiche
              </h4>
              <div className="space-y-2" data-id="element-5012" data-mp="24xqa4ud7pn51">
                <div className="flex items-center gap-2" data-id="element-5013" data-mp="3cdxseuiv5t1q">
                  <div className={`w-2 h-2 rounded-full ${selectedItem.sendEmailOnExpiry !== false ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-5014" data-mp="3cfh9gnccg84k"></div>
                  <span className="text-sm text-slate-700" data-id="element-5015" data-mp="14quwdqnnmfs2">
                    Invia email automatica alla scadenza al cliente
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-5016" data-mp="vyaumhc2fv30">
                  <div className={`w-2 h-2 rounded-full ${selectedItem.sendEmailToSEF !== false ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-5017" data-mp="r099zryzrnpf"></div>
                  <span className="text-sm text-slate-700" data-id="element-5018" data-mp="lz24jzkbn0lx">
                    Invia notifica a SEF alla scadenza
                  </span>
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-5019" data-mp="d2krurwb6uo2">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-5020" data-mp="8384m61izlky">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-5022" data-mp="1g7u6i8gvb9qq" />} data-id="element-5021" data-mp="22g6ewpn86i8a">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica documento' : 'Nuovo documento'} size="2xl" data-id="element-5023" data-mp="16ialox9lh5t8">
        <AnimatePresence mode="wait" data-id="element-5024" data-mp="wlek7bi45jy6">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-5025" data-mp="ild9z5fyl061">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5026" data-mp="2hyr1wssz3qh5">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-5027" data-mp="b85nmfrxis15" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5028" data-mp="ozsn0oh64go5">
                Documento salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-5029" data-mp="yyi44f18px3c">
              <div className="space-y-8" data-id="element-5030" data-mp="30rz898q5wftc">
                <section data-id="element-5031" data-mp="30t0v0agjiqqw">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5032" data-mp="2vsmlsp37dara">
                    <Receipt className="h-4 w-4" data-id="element-5033" data-mp="uav1b01rlksg" /> Dati documento
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5034" data-mp="pctlqp3js3lq">
                    {selectedItem ? <Input label="Codice" value={formData.code} disabled data-id="element-5035" data-mp="kl21ewaziu41" /> : <ProgressiveNumberInput type={getProgressiveType(formData.type)} value={formData.code} onChange={val => setFormData({
                  ...formData,
                  code: val
                })} label={getCodeLabel(formData.type)} data-id="element-5036" data-mp="gjzxi264htvq" />}

                    <Select label="Tipo documento" value={formData.type || 'Fattura'} onChange={e => {
                  const newType = e.target.value;
                  // Reset status when type changes
                  const newStatusOptions = getStatusOptions(newType);
                  setFormData({
                    ...formData,
                    type: newType,
                    status: newStatusOptions[0].value
                  });
                }} options={[{
                  value: 'Fattura',
                  label: 'Fattura'
                }, {
                  value: 'Proforma',
                  label: 'Proforma'
                }, {
                  value: 'Nota di credito',
                  label: 'Nota di credito'
                }]} required data-id="element-5037" data-mp="1w0ibvww12pu6" />

                    <SearchableSelect label="Cliente" value={formData.client || ''} onChange={e => setFormData({
                  ...formData,
                  client: e.target.value
                })} options={[{
                  value: 'Azienda 1 SRL',
                  label: 'Azienda 1 SRL'
                }, {
                  value: 'Azienda 2 SRL',
                  label: 'Azienda 2 SRL'
                }, {
                  value: 'Azienda 3 SRL',
                  label: 'Azienda 3 SRL'
                }, {
                  value: 'Azienda 4 SRL',
                  label: 'Azienda 4 SRL'
                }, {
                  value: 'Azienda 5 SRL',
                  label: 'Azienda 5 SRL'
                }]} required data-id="element-5038" data-mp="kac7b028z9c8" />

                    <Select label="Stato" value={formData.status || 'da_pianificare'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={getStatusOptions(formData.type)} required data-id="element-5039" data-mp="3chz3mm4vgiy8" />

                    {formData.type === 'Fattura' && formData.status === 'sollecitare' && <div className="col-span-full bg-orange-50 p-4 rounded-xl border border-orange-200" data-id="element-5040" data-mp="440cpywrbnrl">
                          <span className="text-xs font-semibold text-orange-600 uppercase block mb-3" data-id="element-5041" data-mp="2oselu6kgfrqj">
                            Stato solleciti
                          </span>
                          <div className="flex gap-6" data-id="element-5042" data-mp="3444klcstnvy5">
                            <label className="flex items-center gap-2 cursor-pointer" data-id="element-5043" data-mp="2zaa2uews1r1p">
                              <input type="checkbox" checked={formData.sollecito1} onChange={e => setFormData({
                        ...formData,
                        sollecito1: e.target.checked,
                        sollecito2: e.target.checked ? formData.sollecito2 : false,
                        sollecito3: e.target.checked ? formData.sollecito3 : false
                      })} className="rounded border-orange-300 text-orange-500 focus:ring-orange-500" data-id="element-5044" data-mp="2ubgnegxxjpyw" />
                              <span className="text-sm font-medium text-orange-900" data-id="element-5045" data-mp="i42yf44wghig">
                                Sollecito 1
                              </span>
                            </label>

                            <label className={`flex items-center gap-2 ${!formData.sollecito1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-5046" data-mp="qwd8mlfsomzp">
                              <input type="checkbox" checked={formData.sollecito2} disabled={!formData.sollecito1} onChange={e => setFormData({
                        ...formData,
                        sollecito2: e.target.checked,
                        sollecito3: e.target.checked ? formData.sollecito3 : false
                      })} className="rounded border-orange-300 text-orange-500 focus:ring-orange-500" data-id="element-5047" data-mp="10ry749lnn54p" />
                              <span className="text-sm font-medium text-orange-900" data-id="element-5048" data-mp="nolxjomfq0ed">
                                Sollecito 2
                              </span>
                            </label>

                            <label className={`flex items-center gap-2 ${!formData.sollecito2 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-5049" data-mp="wqld0chswwf7">
                              <input type="checkbox" checked={formData.sollecito3} disabled={!formData.sollecito2} onChange={e => setFormData({
                        ...formData,
                        sollecito3: e.target.checked
                      })} className="rounded border-orange-300 text-orange-500 focus:ring-orange-500" data-id="element-5050" data-mp="16irf4f1rou84" />
                              <span className="text-sm font-medium text-orange-900" data-id="element-5051" data-mp="1gcwz3edru272">
                                Sollecito 3
                              </span>
                            </label>
                          </div>
                        </div>}
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-5052" data-mp="1belmwx007flo"></div>

                <section data-id="element-5053" data-mp="xdswanctp4nu">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5054" data-mp="xdj2eoonqdcv">
                    <FileText className="h-4 w-4" data-id="element-5055" data-mp="12cchgljlecf5" /> Riferimenti e dettagli
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5056" data-mp="1hudgdicdujv7">
                    <Input label="Numero di contratto" placeholder="Inserisci numero contratto" value={formData.contractNumber || ''} onChange={e => setFormData({
                  ...formData,
                  contractNumber: e.target.value
                })} data-id="element-5057" data-mp="1cvj0op9ppbfb" />

                    {formData.type === 'Fattura' && <Input label="Numero di proforma" placeholder="Inserisci numero proforma" value={formData.proformaNumber || ''} onChange={e => setFormData({
                  ...formData,
                  proformaNumber: e.target.value
                })} data-id="element-5058" data-mp="p79iryfmd7tg" />}

                    {formData.type === 'Nota di credito' && <Input label="Numero fattura" placeholder="Inserisci numero fattura" value={formData.invoiceNumber || ''} onChange={e => setFormData({
                  ...formData,
                  invoiceNumber: e.target.value
                })} data-id="element-5059" data-mp="2ruq0sqn6f0t9" />}

                    <div className="flex flex-col gap-1.5" data-id="element-5060" data-mp="2d2o0j5h725sp">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-5061" data-mp="2hvqzxu8nk9gt">
                        Importo con IVA
                      </label>
                      <div className="flex items-center gap-2 h-[42px]" data-id="element-5062" data-mp="1kdh4qe94jub5">
                        <span className={formData.withVat ? 'text-green-600 font-bold' : 'text-slate-400'} data-id="element-5063" data-mp="1pctogq4p8l9i">
                          Sì
                        </span>
                        <button onClick={() => setFormData({
                      ...formData,
                      withVat: !formData.withVat
                    })} className={`w-10 h-5 rounded-full relative transition-colors ${formData.withVat ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-5064" data-mp="k490ggphci6q">
                          <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${formData.withVat ? 'left-6' : 'left-1'}`} data-id="element-5065" data-mp="131sbtqzeucwb" />
                        </button>
                        <span className={!formData.withVat ? 'text-red-600 font-bold' : 'text-slate-400'} data-id="element-5066" data-mp="34wkloq1c8dg2">
                          No
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-5067" data-mp="3gactrh3ko7bu"></div>

                <section data-id="element-5068" data-mp="3g7qltgcrrpt3">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5069" data-mp="3bazv7z3c9btf">
                    <Euro className="h-4 w-4" data-id="element-5070" data-mp="36aual2vhkpku" /> Dettagli economici
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5071" data-mp="31d28xh51hp3g">
                    <Input label="Importo (€)" type="number" placeholder="0.00" value={formData.amount?.replace('€ ', '') || ''} onChange={e => setFormData({
                  ...formData,
                  amount: `€ ${e.target.value}`
                })} required data-id="element-5072" data-mp="1uwgqm93usua3" />
                    <Input label="Data emissione" type="date" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-5073" data-mp="824r32r8nufk" />

                    <div className="flex flex-col gap-2" data-id="element-5074" data-mp="3lcu2cpi6y7df">
                      <Select label="Data scadenza" value={formData.dueDateType || 'Immediata'} onChange={e => setFormData({
                    ...formData,
                    dueDateType: e.target.value
                  })} options={[{
                    value: 'Immediata',
                    label: 'Immediata'
                  }, {
                    value: 'Specifica',
                    label: 'Specifica'
                  }, {
                    value: 'Inizio mese',
                    label: 'Inizio mese'
                  }]} data-id="element-5075" data-mp="3qejfqbsvyx4o" />
                      {formData.dueDateType === 'Specifica' && <Input type="date" value={formData.dueDate || ''} onChange={e => setFormData({
                    ...formData,
                    dueDate: e.target.value
                  })} data-id="element-5076" data-mp="3kkeaky7w82db" />}
                    </div>

                    <Input label="Data di pagamento" type="date" value={formData.paymentDate || ''} onChange={e => setFormData({
                  ...formData,
                  paymentDate: e.target.value
                })} disabled={formData.status === 'pagato'} description={formData.status === 'pagato' ? 'Impostata automaticamente' : undefined} data-id="element-5077" data-mp="1wrlw3em6exqh" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-5078" data-mp="2z7xk478xmqkt"></div>

                <section data-id="element-5079" data-mp="2z6m7rj1f8ai3">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5080" data-mp="nl3qafps3wx4">
                    <Mail className="h-4 w-4" data-id="element-5081" data-mp="ilqtp393vbhc" /> Notifiche automatiche
                  </h4>
                  <div className="space-y-3" data-id="element-5082" data-mp="duqwdww2qreb">
                    <div className="flex items-center gap-2" data-id="element-5083" data-mp="dtfww99nw5t0">
                      <input type="checkbox" id="sendEmailOnExpiry" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailOnExpiry !== false} onChange={e => setFormData({
                    ...formData,
                    sendEmailOnExpiry: e.target.checked
                  })} data-id="element-5084" data-mp="8wz4v3zcd7an" />
                      <label htmlFor="sendEmailOnExpiry" className="text-sm text-slate-700" data-id="element-5085" data-mp="1p836gpifg68x">
                        Invia email automatica alla scadenza al cliente
                      </label>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-5086" data-mp="3jkdymv2brlom">
                      <input type="checkbox" id="sendEmailToSEF" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailToSEF !== false} onChange={e => setFormData({
                    ...formData,
                    sendEmailToSEF: e.target.checked
                  })} data-id="element-5087" data-mp="34upyvy9skv30" />
                      <label htmlFor="sendEmailToSEF" className="text-sm text-slate-700" data-id="element-5088" data-mp="2c4prnsrtj5wz">
                        Invia notifica a SEF alla scadenza
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-5089" data-mp="u7mhp82olcdq">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-5090" data-mp="186m6cfq71r9w">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-5092" data-mp="bhegwwmgey0p" />} data-id="element-5091" data-mp="ggras1bqt5cs">
                    Salva documento
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-5093" data-mp="3swuwvrpyf586">
        <AnimatePresence mode="wait" data-id="element-5094" data-mp="3otf63c0kfz3e">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-5095" data-mp="hm7sqvfniw78">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5096" data-mp="10j8zolsrn6dt">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-5097" data-mp="1o8jw5r332em5" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5098" data-mp="1ydjqtsax23zr">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-5099" data-mp="1tgjhn8irsl4f">
              <div className="space-y-6" data-id="element-5100" data-mp="2h3rohuvuk28k">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-5101" data-mp="2h2q1qt5gxrb0">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-5102" data-mp="2c5pvah4pimjd" />
                  <div data-id="element-5103" data-mp="27dg8h9yw22lb">
                    <p className="text-sm text-red-700 font-medium" data-id="element-5104" data-mp="27cwta10mhsaz">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-5105" data-mp="2zu93kwnrazbg">
                      Il documento <strong data-id="element-5106" data-mp="2zwdisar3vkr1">{itemToDelete?.code}</strong> verrà
                      eliminato definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-5107" data-mp="njyk12kxnsr">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-5108" data-mp="3ru4mvd192hm3">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-5110" data-mp="jjt9wgm3k092" />} data-id="element-5109" data-mp="5kjx7qx3no9u">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}